import { FC } from 'react';
import {
	Box,
	DialogContentText,
	Link,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
	getEuDdpConfirmShipmentError,
	getEuDdpOrder,
	getEuDdpRequestShipmentData,
} from 'redux/reducers/euDdp/selectors';
import { PATHS } from 'routes/constants';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { WarningAlert } from 'ui/WarningAlert';
import { S10Instructions } from '../S10Instructions';

// bank fee rate of the payment gateway quote: a fraction (0.03) shown as "3%"
const formatFeePercent = (fraction: number) =>
	`${(Math.round((fraction || 0) * 100 * 100) / 100).toLocaleString('en', { maximumFractionDigits: 2 })}%`;

export const StepPreview: FC = () => {
	const order = useSelector(getEuDdpOrder);
	const requestShipmentData = useSelector(getEuDdpRequestShipmentData);
	const confirmShipmentError = useSelector(getEuDdpConfirmShipmentError);
	return (
		<>
			<DialogContentText>
				{order?.order.schemes === 'FC-REP'
					? 'All VAT, duties, delivery fees and handling fees are collected now. No VAT or duties will be reported or paid through IOSS for this shipment, and the recipient will not be asked to pay any charges upon delivery.'
					: 'VAT is handled through IOSS. Any applicable customs duties are included in the delivery cost below. The recipient will not be asked to pay any charges upon delivery.'}
			</DialogContentText>
			{!!requestShipmentData && (
				<Box sx={{ mt: 2 }}>
					<TableContainer>
						<Table size="small">
							<TableBody>
								<TableRow>
									<TableCell sx={{ padding: '8px 0', borderBottom: 0 }}>Delivery Fee:</TableCell>
									<TableCell align="right" sx={{ padding: '8px 0', borderBottom: 0 }}>
										<AmountAndCurrency amount={requestShipmentData.shippingCost} currency="EUR" />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell sx={{ padding: '8px 0', borderBottom: 0 }}>
										Taxes and Duties:
									</TableCell>
									<TableCell align="right" sx={{ padding: '8px 0', borderBottom: 0 }}>
										<AmountAndCurrency amount={requestShipmentData.dutiesTaxes} currency="EUR" />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell sx={{ padding: '8px 0', borderBottom: 0 }}>
										Customs Handling Fee:
									</TableCell>
									<TableCell align="right" sx={{ padding: '8px 0', borderBottom: 0 }}>
										<AmountAndCurrency amount={requestShipmentData.easFee} currency="EUR" />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell sx={{ padding: '8px 0', borderBottom: 0 }}>Bank Fee:</TableCell>
									<TableCell align="right" sx={{ padding: '8px 0', borderBottom: 0 }}>
										<AmountAndCurrency amount={requestShipmentData.bankFee} currency="EUR" />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell sx={{ padding: '8px 0', fontWeight: 'bold' }}>
										Total Delivery Cost:
									</TableCell>
									<TableCell align="right" sx={{ padding: '8px 0', fontWeight: 'bold' }}>
										<AmountAndCurrency
											amount={requestShipmentData.totalAmount}
											currency="EUR"
											bold
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
					{requestShipmentData.paymentMethodMissing ? (
						<WarningAlert sx={{ mt: 2 }}>
							We have changed our payment provider. Please add your card again in{' '}
							<Link component={RouterLink} to={PATHS.PAYMENT_METHODS_PAGE}>
								Payment methods
							</Link>
							.
						</WarningAlert>
					) : (
						!!requestShipmentData.paymentMethodLabel && (
							<Typography sx={{ mt: 2, fontSize: '.875rem', color: 'text.secondary' }}>
								Bank fee {formatFeePercent(requestShipmentData.bankFeePercent)} for{' '}
								{requestShipmentData.paymentMethodLabel}
							</Typography>
						)
					)}
					{!requestShipmentData.s10Expected && (
						<Typography
							sx={{ mt: 2, fontSize: '.875rem', color: 'text.secondary', fontWeight: 'bold' }}
						>
							Please note that the delivery cost becomes non-refundable once confirmed.
						</Typography>
					)}
					{requestShipmentData.postPaymentAllowed && (
						<Typography
							sx={{ mt: 2, fontSize: '.875rem', color: 'text.secondary', fontWeight: 'bold' }}
						>
							The amount above will be invoiced to you separately.
						</Typography>
					)}
				</Box>
			)}
			{!!requestShipmentData?.s10Expected && <S10Instructions />}
		</>
	);
};
